import { type ChangeEvent, type FormEvent, useCallback } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import { type CardPaymentMethodEditorViewModel } from '@/features/billing/payment-methods/CardPaymentMethodEditorViewModel'
import { CardPaymentMethodEditorContent } from '@/features/billing/payment-methods/NewPaymentMethodDialog/CardPaymentMethodEditorContent'
import { CardInputContainer } from '@/features/billing/payment-methods/NewPaymentMethodDialog/CardPaymentMethodEditorContent/CardInput/CardInputContainer'
import { Form, FormColumn, FormColumns, FormRow } from '@/components/Form'
import { FormGroupContainer } from '@/components/FormContainer'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { CountryInput } from '@/components/inputs/CountryInput'
import { TextInput } from '@/components/inputs/TextInput'
import { observer } from 'mobx-react-lite'

export interface CardPaymentMethodEditorContentContainerProps {
  vm: CardPaymentMethodEditorViewModel
}

export const CardPaymentMethodEditorContentContainer = observer(
  function CardPaymentMethodEditorContentContainer({
    vm,
  }: CardPaymentMethodEditorContentContainerProps) {
    return (
      <CardPaymentMethodEditorContent>
        <Elements stripe={vm.stripe!} options={{ locale: 'en' }}>
          <CardPaymentMethodFormContainer vm={vm} />
        </Elements>
      </CardPaymentMethodEditorContent>
    )
  },
)

interface CardPaymentMethodFormContainerProps {
  vm: CardPaymentMethodEditorViewModel
}

const CardPaymentMethodFormContainer = observer(
  function CardPaymentMethodFormContainer({
    vm,
  }: CardPaymentMethodFormContainerProps) {
    const disabled = vm.save.pending
    const handleSubmit = useCallback(
      (event: FormEvent) => {
        event.preventDefault()
        // The dialog shows the save error.
        vm.save().catch(() => undefined)
      },
      [vm],
    )

    const handleChangeStateOrTerritory = useCallback(
      (e: ChangeEvent<HTMLInputElement>) => {
        vm.setStateOrTerritory(e.target.value)
      },
      [vm],
    )

    return (
      <Form onSubmit={handleSubmit} isSubmitDisabled={disabled}>
        <FormRow>
          <TextInputContainer
            model={vm}
            field="fullName"
            label="Name"
            name="name"
            autoFocus
            placeholder="Please enter the full name as it is displayed on your card."
            isDisabled={disabled}
            error={vm.validation.getError('fullName')}
            data-test="add-card-form-full-name-input"
          />
        </FormRow>
        <FormRow>
          <TextInputContainer
            model={vm}
            field="address"
            name="address"
            label="Address"
            placeholder="Street name, number and suite/unit/apt number if applicable."
            isDisabled={disabled}
            error={vm.validation.getError('address')}
            data-test="add-card-form-address-input"
          />
        </FormRow>
        <FormRow>
          <TextInputContainer
            model={vm}
            field="city"
            name="city"
            label="City"
            placeholder="Enter the city name"
            isDisabled={disabled}
            error={vm.validation.getError('city')}
            data-test="add-card-form-city-input"
          />
        </FormRow>
        <FormRow>
          <FormColumns>
            {vm.isStateOrTerritoryRelevant && (
              <FormColumn>
                <FormGroupContainer
                  label="State"
                  error={vm.validation.getError('stateOrTerritory')}
                >
                  <TextInput
                    placeholder="2-digit state code (e.g. FL for Florida)"
                    isDisabled={disabled}
                    name="state"
                    value={vm.stateOrTerritory}
                    onChange={handleChangeStateOrTerritory}
                    data-test="add-card-form-state-input"
                  />
                </FormGroupContainer>
              </FormColumn>
            )}

            <FormColumn>
              <TextInputContainer
                model={vm}
                field="zipCode"
                name="zip"
                label="Zip"
                placeholder="Zip Code"
                isDisabled={disabled}
                error={vm.validation.getError('zipCode')}
                data-test="add-card-form-zip-input"
              />
            </FormColumn>
          </FormColumns>
        </FormRow>
        <FormRow>
          <FormGroupContainer
            htmlFor="country"
            label="Country"
            error={vm.validation.getError('country')}
          >
            <CountryInput
              isDisabled={disabled}
              value={vm.country}
              onChange={vm.setCountry}
              testId="add-card-form-country-input"
            />
          </FormGroupContainer>
        </FormRow>
        <FormRow>
          <CardInputContainer isDisabled={disabled} vm={vm} />
        </FormRow>
      </Form>
    )
  },
)
